import { useState, useEffect, useCallback } from 'react';
import { StorageHelper } from '@@/lib/StorageHelper';
import { CommonApiClient } from '@@/api/api-client';
import { getUserPoolConfigResponse } from '@@/api/api-common';
import { useLocalStorage } from '@@/hooks/use-local-storage';

export interface UseUserPoolConfigResult {
  userPoolConfig: getUserPoolConfigResponse | null;
  isLoading: boolean;
  error: string | null;
  companyEmail: string | null;
  showInterstitial: boolean;
  handleEmailSubmit: (email: string) => Promise<void>;
  clearCompanyEmail: () => void;
}

export const useUserPoolConfig = (): UseUserPoolConfigResult => {
  const client = new CommonApiClient();
  const [companyEmail, setCompanyEmail] = useLocalStorage<string | null>('cxp-company-email', null);
  const [userPoolConfig, setUserPoolConfig] = useLocalStorage<getUserPoolConfigResponse | null>(
    'cxp-user-pool-config',
    null
  );
  const [showInterstitial, setShowInterstitial] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const validateConfigInBackground = useCallback(
    async (email: string, abortSignal: AbortSignal) => {
      try {
        const result = await client.common.fetchUserPoolConfig(email);

        if (abortSignal.aborted) return;

        if (!result.ok) {
          console.log('Background validation failed:', result.message);
          StorageHelper.clearCompanyEmail();
          setCompanyEmail(null);
          setUserPoolConfig(null);
          setError(result.message || 'Failed to load configuration');
          setShowInterstitial(true);
        } else {
          console.log('Background validation successful');
          setUserPoolConfig(result.data);
        }
      } catch (err) {
        if (!abortSignal.aborted) {
          console.error('Background validation error:', err);
        }
      }
    },
    [client]
  );

  const fetchUserPoolConfig = useCallback(
    async (email: string, abortSignal: AbortSignal) => {
      try {
        setIsLoading(true);
        const result = await client.common.fetchUserPoolConfig(email);

        if (abortSignal.aborted) return;

        if (result.ok && result.data) {
          setUserPoolConfig(result.data);
          StorageHelper.setCompanyId(result.data.companyId);
          setError(null);
        } else {
          StorageHelper.clearCompanyEmail();
          setCompanyEmail(null);
          setError(result.message || 'Failed to load configuration');
          setShowInterstitial(true);
        }
      } catch (err) {
        if (!abortSignal.aborted) {
          console.error('Fetch error:', err);
          setError('Cannot fetch User Pool configuration.');
          setShowInterstitial(true);
        }
      } finally {
        if (!abortSignal.aborted) {
          setIsLoading(false);
        }
      }
    },
    [client]
  );

  const handleEmailSubmit = useCallback(
    async (email: string) => {
      if (!email.trim()) {
        setError('Company Email is required');
        return;
      }

      const controller = new AbortController();
      setError(null);

      try {
        await fetchUserPoolConfig(email, controller.signal);

        if (!controller.signal.aborted) {
          StorageHelper.setCompanyEmail(email);
          setCompanyEmail(email);
          setShowInterstitial(false);
        }
      } catch (err) {
        if (!controller.signal.aborted) {
          setError('Failed to load configuration');
        }
      }
    },
    [fetchUserPoolConfig]
  );

  const clearCompanyEmail = useCallback(() => {
    StorageHelper.clearCompanyEmail();
    StorageHelper.clearCompanyId();
    setCompanyEmail(null);
    setUserPoolConfig(null);
    setError(null);
    setShowInterstitial(true);
  }, []);

  useEffect(() => {
    const controller = new AbortController();

    const initializeUserPoolConfig = async () => {
      if (!companyEmail) {
        setShowInterstitial(true);
        return;
      }

      if (userPoolConfig) {
        console.log('Using cached config:', userPoolConfig);
        StorageHelper.setCompanyId(userPoolConfig.companyId);

        validateConfigInBackground(companyEmail, controller.signal);
        return;
      }

      await fetchUserPoolConfig(companyEmail, controller.signal);
    };

    initializeUserPoolConfig();

    return () => {
      controller.abort();
    };
  }, []);

  return {
    userPoolConfig,
    isLoading,
    error,
    companyEmail,
    showInterstitial,
    handleEmailSubmit,
    clearCompanyEmail
  };
};
